import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { blogPosts, getBlogPostBySlug, getBlogImage } from '../data/allBlogPosts';
import type { ContentBlock } from '../data/seoBlogArticles';
import { buildArticleSchema, buildBreadcrumbList } from '../lib/structuredData';
const renderContentBlock = (block: ContentBlock, index: number) => {
  switch (block.type) {
    case 'heading':
      return <h2 key={index} className="text-2xl md:text-3xl font-bold text-[#111827] mt-12 mb-5" data-id="element-1238" data-mp="1cfhijyax9lj3">
          {block.content}
        </h2>;
    case 'heading3':
      return <h3 key={index} className="text-xl md:text-2xl font-bold text-[#111827] mt-10 mb-4" data-id="element-1239" data-mp="2040r8exvw0bp">
          {block.content}
        </h3>;
    case 'paragraph':
      return <p key={index} className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-1240" data-mp="kc7fbhp3w40r">
          {block.content}
        </p>;
    case 'list':
      return <ul key={index} className="space-y-3 mb-8" data-id="element-1241" data-mp="g7zpfh6wwi0y">
          {block.items?.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1242" data-mp="u3ageji1cj01">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#004AC7] flex-shrink-0" aria-hidden="true" data-id="element-1243" data-mp="p9fz1mpwkg44" />
              <span className="text-lg text-[#4B5563] leading-relaxed" data-id="element-1244" data-mp="13y2d7l3sn1tr">
                {item}
              </span>
            </li>)}
        </ul>;
    case 'callout':
      return <div key={index} className="bg-[#F7F8FA] border-l-4 border-[#004AC7] p-6 rounded-r-lg my-10" data-id="element-1245" data-mp="1d0ldbxg730cb">
          <div className="flex items-start gap-4" data-id="element-1246" data-mp="180gtbxg2vnhc">
            <InfoIcon className="w-6 h-6 text-[#004AC7] flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-1247" data-mp="17yw1wgp28knp" />
            <p className="text-lg text-[#111827] font-medium leading-relaxed m-0" data-id="element-1248" data-mp="3etj0bcnaiypc">
              {block.content}
            </p>
          </div>
        </div>;
    default:
      return null;
  }
};
export const BlogPostPage: React.FC = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = getBlogPostBySlug(slug || '');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!post) {
    return <Navigate to="/bibliotheek" replace data-id="element-1249" data-mp="1roqb6waet4nl" />;
  }
  const related = post.relatedPosts.map(relatedSlug => getBlogPostBySlug(relatedSlug)).filter((item): item is NonNullable<typeof item> => Boolean(item)).slice(0, 3);
  const fallbackRelated = blogPosts.filter(item => item.slug !== post.slug && item.category === post.category).slice(0, 3);
  const relatedToShow = related.length > 0 ? related : fallbackRelated;
  const heroImage = getBlogImage(post);
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1250" data-mp="2bcfra68zhggz">
      <SEO title={`${post.title} | Noord Digital`} description={post.metaDescription} keywords={post.keywords} canonical={`https://noorddigital.nl/blog/${post.slug}`} ogType="article" ogImage={heroImage} jsonLd={[buildArticleSchema({
      headline: post.title,
      description: post.metaDescription,
      path: `/blog/${post.slug}`,
      section: post.category,
      image: heroImage
    }), buildBreadcrumbList([{
      name: 'Bibliotheek',
      path: '/bibliotheek'
    }, {
      name: post.category,
      path: '/bibliotheek'
    }, {
      name: post.title,
      path: `/blog/${post.slug}`
    }])]} data-id="element-1251" data-mp="2fflwrbo9v3me" />
      <Header data-id="element-1252" data-mp="rga3a42g8gda" />

      <main data-id="element-1253" data-mp="riw99z9oynte">
        {/* Hero */}
        <div className="bg-[#F7F8FA] pt-28 pb-14 md:pt-32 border-b border-gray-200" data-id="element-1254" data-mp="nfggxf4nn9mi">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1255" data-mp="iohv600wdjbv">
            <nav aria-label="Kruimelpad" className="mb-8" data-id="element-1256" data-mp="imdlt2k8bs47">
              <ol className="flex items-center gap-1.5 text-sm text-[#6B7280] flex-wrap" data-id="element-1257" data-mp="dobxo37y52lv">
                <li data-id="element-1258" data-mp="1b5u9d6r5jahm">
                  <Link to="/" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-1259" data-mp="1b6c0xq2vd0j7">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-1260" data-mp="pya3ah6d56a2">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" data-id="element-1261" data-mp="px8e78siij9c" />
                </li>
                <li data-id="element-1262" data-mp="uyxsqyey1f26">
                  <Link to="/bibliotheek" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-1263" data-mp="uv9yty5l6qmx">
                    Bibliotheek
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-1264" data-mp="lx7yzlxla418">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" data-id="element-1265" data-mp="19m0ph2vfzjog" />
                </li>
                <li className="font-medium text-[#111827]" aria-current="page" data-id="element-1266" data-mp="1dnw1b1b01fgh">
                  {post.category}
                </li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-id="element-1267" data-mp="zumt33ysezzj">
              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-1268" data-mp="14tzj1xkygn6c">
                <span className="inline-block bg-[#004AC7] text-white text-xs font-semibold px-3 py-1 rounded-full mb-5" data-id="element-1269" data-mp="26pdjt8uk8kl2">
                  {post.category}
                </span>
                <h1 className="font-bold text-[#111827] text-3xl md:text-4xl lg:text-5xl leading-tight mb-5" data-id="element-1270" data-mp="2gfotzd4u7q0e">
                  {post.title}
                </h1>
                <p className="text-[#4B5563] text-lg leading-relaxed mb-5" data-id="element-1271" data-mp="1nrswi67g1u2k">
                  {post.excerpt}
                </p>
                <p className="inline-flex items-center gap-2 text-sm text-[#6B7280]" data-id="element-1272" data-mp="1jrit91b1dhre">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1273" data-mp="1xqih46qq5smi" />
                  {post.readingTime} minuten lezen
                </p>
              </motion.div>

              <div className="lg:col-span-5" data-id="element-1274" data-mp="1sti7jluo27qn">
                <img src={heroImage} alt={`${post.title}, artikel van Noord Digital`} className="w-full h-56 md:h-72 object-cover rounded-2xl shadow-lg" data-id="element-1275" data-mp="394ckbn4g15kt" />
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <article className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[760px] py-14 md:py-16" data-id="element-1276" data-mp="3c9393p50nf6s">
          {post.content.map(renderContentBlock)}

          <div className="mt-14 bg-[#004AC7] rounded-2xl p-8 md:p-10" data-id="element-1277" data-mp="3l7f2fyx9or2x">
            <h2 className="text-white font-bold text-2xl md:text-3xl mb-3" data-id="element-1278" data-mp="3q6jdyw7nz0wf">
              Wil je dit voor je eigen bedrijf op orde hebben?
            </h2>
            <p className="text-white/85 leading-relaxed mb-6" data-id="element-1279" data-mp="2skly76ko59o6">
              In een gesprek van een half uur kijken we naar je huidige
              vindbaarheid en vertellen we wat we zouden aanpakken. Je zit
              daarna tot niets verplicht.
            </p>
            <a href="/#contact" className="inline-flex items-center gap-2 bg-white text-[#004AC7] font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors duration-200" data-id="element-1280" data-mp="37871b0hjn8w3">
              Plan een kennismaking
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1281" data-mp="2eqbgvab04hs5" />
            </a>
          </div>
        </article>

        {/* Related */}
        {relatedToShow.length > 0 && <section className="w-full bg-[#F7F8FA] py-16 md:py-20 border-t border-gray-200" data-id="element-1282" data-mp="228qz7arumgmz">
            <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1283" data-mp="1x9ebyox2f7jv">
              <h2 className="font-bold text-[#111827] text-2xl md:text-3xl mb-10" data-id="element-1284" data-mp="1sb33oimf553r">
                Verder lezen
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1285" data-mp="1oaasvkxwes5d">
                {relatedToShow.map(item => <Link key={item.slug} to={`/blog/${item.slug}`} className="group bg-white rounded-2xl border border-gray-200 p-6 flex flex-col hover:border-[#004AC7] transition-colors duration-200" data-id="element-1286" data-mp="1jhhpb0e61puv">
                    <span className="text-xs font-semibold text-[#004AC7] mb-3" data-id="element-1287" data-mp="19lebuydv64sm">
                      {item.category}
                    </span>
                    <h3 className="font-bold text-[#111827] text-lg mb-3" data-id="element-1288" data-mp="14n33yt74q4d1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#6B7280] leading-relaxed mb-5 flex-grow" data-id="element-1289" data-mp="2oftcykna0f8z">
                      {item.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-[#004AC7] font-semibold text-sm mt-auto" data-id="element-1290" data-mp="2tdd4uncwcqje">
                      Lees verder
                      <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" data-id="element-1291" data-mp="2jggsg6jvnk9k" />
                    </span>
                  </Link>)}
              </div>

              <div className="mt-12" data-id="element-1292" data-mp="2ahd8l0cvz0u0">
                <Link to="/bibliotheek" className="inline-flex items-center gap-2 text-[#004AC7] font-semibold hover:text-[#003A9E] transition-colors duration-200" data-id="element-1293" data-mp="2aienjfx0w7h0">
                  <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1294" data-mp="20ra70brp8ek2" />
                  Terug naar de bibliotheek
                </Link>
              </div>
            </div>
          </section>}
      </main>

      <Footer data-id="element-1295" data-mp="1qs2rbhxhs7ts" />
    </div>;
};